import React, { useEffect, useRef, useState } from 'react';

import { Box } from '@rbilabs/components-library/build/components/layout';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import ActionButton, { ActionLink } from 'components/action-button';
import { CheckoutErrorsModal } from 'components/checkout-errors-modal';
import StoreConfirmationModal from 'components/store-confirmation-modal';
import { useBlockedByRegion } from 'hooks/use-block-by-region';
import useDialogModal from 'hooks/use-dialog-modal';
import { useGetPaymentErrors } from 'hooks/use-get-payment-errors';
import useIsSignUpAfterCart from 'hooks/use-is-sign-up-after-cart';
import { useAuthContext } from 'state/auth';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useMParticleContext } from 'state/mParticle';
import { OrderStatus, useOrderContext } from 'state/order';
import { usePaymentContext } from 'state/payment';
import { useServiceModeContext } from 'state/service-mode';
import { useUIContext } from 'state/ui';
import LocalStorage, { StorageKeys } from 'utils/local-storage';
import { relativeRoutes } from 'utils/routing';

import { EnsureBottomPadding, SectionContainer } from '../styled';

import DeliveryCancellationPolicy from './delivery-cancellation-policy';
import { useOrderPayment } from './order-payment/use-order-payment';
import SignUpToContinueActionLink from './sign-up-to-continue-action-link';
import { AddMoreText, CartPriceTooLow, SignUpToContinueDisclaimer } from './styled';
import { TableNumber } from './table-number';
import { IPaymentProps } from './types';
import { useValidateCommitOrder } from './use-validate-commit-order';
import { logCartContinue } from './utils';
import { VehicleDescription } from './vehicle-description';

const Payment: React.FC<IPaymentProps> = ({
  className,
  disabled = false,
  serverOrder,
  pricedAndAvailable,
  areDeliveryDetailsValid,
  offerIsValid,
  orderStatus,
}) => {
  const { formatCurrencyForLocale } = useUIContext();
  const { serviceMode, isDelivery, isCurbside, isTableService, isCatering } =
    useServiceModeContext();
  const order = useOrderContext();
  const { cartCateringPriceTooLow, alertOrderDeliveryMinimum, alertOrderCateringMinimum } = order;
  const auth = useAuthContext();
  const { isHostedPage, isFreeOrderPayment } = usePaymentContext();

  const [validationInProgress, setValidationInProgress] = useState(false);
  const [isCheckoutErrorsModalOpen, setIsCheckoutErrorsModalOpen] = useState(false);
  const initialValue = LocalStorage.getItem(StorageKeys.VEHICLE_DESCRIPTION);
  const [
    { value: vehicleDescription, hasError: vehicleDescriptionHasError },
    setVehicleDescription,
  ] = useState({
    value: initialValue,
    hasError: false,
  });
  const [tableNumber, setTableNumber] = useState(
    LocalStorage.getItem(StorageKeys.TABLE_NUMBER) || ''
  );

  const { formatMessage } = useIntl();
  const navigate = useNavigate();

  const sectionRef = useRef<HTMLDivElement>(null);

  const checkoutDeliveryPriceMinimum = useFlag(LaunchDarklyFlag.OVERRIDE_CHECKOUT_DELIVERY_MINIMUM);
  const checkoutCateringPriceMinimum = useFlag(LaunchDarklyFlag.OVERRIDE_CHECKOUT_CATERING_MINIMUM);
  const enableRequireAddress2 = useFlag(
    LaunchDarklyFlag.ENABLE_DELIVERY_DETAILS_FORM_REQUIRE_ADDRESS_2
  );
  const calculateTotalWithTaxes = useFlag(LaunchDarklyFlag.ENABLE_CALCULATE_ORDER_TOTAL_WITH_TAXES);

  const { isBlockedByRegion } = useBlockedByRegion();

  const { validateCommitOrder, ErrorDialog } = useValidateCommitOrder({
    onCoolingPeriodConfirm: () => handleValidateCommitOrder(true),
  });
  const evaluatingIncentives = useAppSelector(selectors.loyalty.selectOffersLoading);

  const { getErrorMessages } = useGetPaymentErrors();
  const isSignUpAfterCart = useIsSignUpAfterCart();

  let checkoutPriceMinimum = isDelivery ? checkoutDeliveryPriceMinimum : 0;

  const cartSubTotal = calculateTotalWithTaxes
    ? order.calculateCartTotal()
    : serverOrder?.cart?.subTotalCents;
  const cartPriceTooLow = cartSubTotal < checkoutPriceMinimum;

  const [StoreConfirmationDialog, openStoreConfirmationModal, , dismissStoreConfirmationDialog] =
    useDialogModal({
      showCancel: true,
      Component: StoreConfirmationModal,
      modalAppearanceEventMessage: 'Confirmation Modal: Store Location',
    });

  const { confirmStoreOnCheckout, shouldConfirmStoreOnCheckout } = useUIContext();

  const { setIsHandlingOrderCommit, setIsFreeOrder, isCommittingOrder } = useOrderPayment({
    serverOrder,
  });

  const dispatch = useAppDispatch();

  const { isCartTotalNegative = false } = order?.calculateCartTotalWithDiscount?.() || {};

  const focusElementWithError = (name: string) => {
    // TODO: Remove queryselector and replace with ref's
    const element: HTMLElement | null | undefined = sectionRef.current?.querySelector(
      `[name=${name}]`
    );

    if (element) {
      element.focus();
    }
  };
  const enableDelivery = useFlag(LaunchDarklyFlag.ENABLE_DELIVERY);
  const isPriceRequested =
    !serverOrder || orderStatus === OrderStatus.PRICE_REQUESTED || validationInProgress;
  const isOrderPriceLoading = !isCartTotalNegative && isPriceRequested;
  const isLoading = isOrderPriceLoading || evaluatingIncentives || isCommittingOrder;

  const mParticle = useMParticleContext();
  const handleErrorContinueClick = () => {
    logCartContinue(mParticle);
    setIsCheckoutErrorsModalOpen(true);
  };

  const handleContinueClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    logCartContinue(mParticle);
    // if loading do nothing
    if (isLoading) {
      e?.preventDefault();
      e?.stopPropagation();
      return;
    }
    if (isCurbside) {
      if (!vehicleDescriptionHasError && vehicleDescription) {
        LocalStorage.setItem(StorageKeys.VEHICLE_DESCRIPTION, vehicleDescription);
      } else {
        return focusElementWithError('vehicleDescription');
      }
    }

    if (isTableService) {
      if (tableNumber) {
        LocalStorage.setItem(StorageKeys.TABLE_NUMBER, tableNumber);
      } else {
        e?.preventDefault();
        e?.stopPropagation();
        return focusElementWithError('tableNumber');
      }
    }

    if (!isDelivery && confirmStoreOnCheckout && (isHostedPage || isFreeOrderPayment)) {
      e?.preventDefault();
      if (isFreeOrderPayment) {
        setIsFreeOrder(true);
      }
      return openStoreConfirmationModal();
    } else {
      handleValidateCommitOrder(false, e);
    }
  };

  const onStoreConfirmation = () => {
    if (!isFreeOrderPayment) {
      shouldConfirmStoreOnCheckout(true);
    }
    dismissStoreConfirmationDialog();
    handleValidateCommitOrder(false);
  };

  function handleValidateCommitOrder(
    skipCoolingPeriod: boolean,
    e?: React.MouseEvent<HTMLButtonElement>
  ) {
    if (isHostedPage || isFreeOrderPayment) {
      if (!serverOrder) {
        throw new Error('Order unavailable');
      }

      e?.preventDefault();
      setValidationInProgress(true);
      validateCommitOrder(serverOrder, order, auth, skipCoolingPeriod)
        .then(() => {
          if (isFreeOrderPayment) {
            setValidationInProgress(false);
            setIsHandlingOrderCommit(true);
          } else {
            navigate(relativeRoutes.orderPayment);
            setValidationInProgress(false);
          }
        })
        .catch(() => {});
    }
  }

  useEffect(alertOrderDeliveryMinimum, [alertOrderDeliveryMinimum]);
  useEffect(alertOrderCateringMinimum, [alertOrderCateringMinimum]);

  const paymentErrorMessages = getErrorMessages({
    areDeliveryDetailsWithError: !areDeliveryDetailsValid,
    invalidAddressLine2:
      enableRequireAddress2 && isDelivery && !order?.deliveryAddress?.addressLine2,
    curbsideOrderError: isCurbside && (vehicleDescriptionHasError || !vehicleDescription),
    tableServiceError: isTableService && !tableNumber,
    invalidOfferError: !offerIsValid,
    pricedAndAvailableError: !pricedAndAvailable,
  });

  const hasErrors = !!paymentErrorMessages.length;

  if (pricedAndAvailable || !isLoading) {
    dispatch(actions.loyalty.setOffersLoading(false));
  }

  const ContinueButton = () =>
    hasErrors ? (
      <ActionButton
        onClick={handleErrorContinueClick}
        fullWidth
        isLoading={isLoading || !pricedAndAvailable}
        disabled={
          (disabled || !offerIsValid || cartPriceTooLow || isBlockedByRegion()) && !isLoading
        }
      >
        {formatMessage({ id: 'continue' })}
      </ActionButton>
    ) : (
      <ActionLink
        onClick={handleContinueClick}
        to={relativeRoutes.orderPayment}
        fullWidth
        isLoading={isLoading || !pricedAndAvailable}
        disabled={disabled || cartPriceTooLow || isBlockedByRegion()}
      >
        {formatMessage({ id: 'continue' })}
      </ActionLink>
    );

  return (
    <SectionContainer
      className={className}
      isCatering={isCatering}
      data-testid="cart-payment"
      ref={sectionRef}
    >
      {isCheckoutErrorsModalOpen && (
        <CheckoutErrorsModal
          checkoutErrors={paymentErrorMessages}
          closeModal={() => {
            setIsCheckoutErrorsModalOpen(false);
          }}
        />
      )}
      <ErrorDialog />
      <StoreConfirmationDialog
        onConfirm={onStoreConfirmation}
        onDismiss={dismissStoreConfirmationDialog}
      />
      <EnsureBottomPadding>
        {isCurbside && !isSignUpAfterCart && (
          <VehicleDescription
            onChange={setVehicleDescription}
            value={vehicleDescription}
            hasError={vehicleDescriptionHasError}
          />
        )}
        {isTableService && !isSignUpAfterCart && (
          <TableNumber onChange={setTableNumber} value={tableNumber} />
        )}
        {(cartPriceTooLow || cartCateringPriceTooLow) && (
          <CartPriceTooLow>
            {formatMessage(
              {
                id: isCatering ? 'cateringMinimumNotReached' : 'deliveryMinumumNotReached',
              },
              {
                min: isCatering
                  ? formatCurrencyForLocale(checkoutCateringPriceMinimum)
                  : formatCurrencyForLocale(checkoutDeliveryPriceMinimum),
              }
            )}
            {isCatering ? null : (
              <AddMoreText>{formatMessage({ id: 'addSomethingExtra' })}</AddMoreText>
            )}
          </CartPriceTooLow>
        )}
        {!isDelivery || (enableDelivery && serviceMode) ? (
          <>
            <Box margin="1rem 0 0">
              {isSignUpAfterCart ? (
                <div>
                  <SignUpToContinueDisclaimer>
                    {formatMessage({ id: 'signUpToContinueDisclaimer' })}
                  </SignUpToContinueDisclaimer>
                  <SignUpToContinueActionLink />
                </div>
              ) : (
                <ContinueButton />
              )}
            </Box>
            {isDelivery && enableDelivery && <DeliveryCancellationPolicy />}
          </>
        ) : (
          <Box margin="1rem 0 0">
            <ActionButton disabled>delivery coming soon</ActionButton>
          </Box>
        )}
      </EnsureBottomPadding>
    </SectionContainer>
  );
};

export default Payment;
